<template>
  <!-- No backdrop-click close: a stray click outside must not discard a
       part-typed email/password. Close with the X button or Escape. -->
  <div class="auth-modal-backdrop">
    <div class="auth-modal-container">
      <div class="auth-modal-header">
        <h2 class="auth-modal-title">
          <i class="fas fa-key"></i>
          Reset Password
        </h2>
        <button class="auth-modal-close" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="auth-form">
        <div v-if="!emailSent">
          <p class="form-description">
            Enter your email address and we'll send you a secure link to reset your password.
          </p>

          <form @submit.prevent="handleForgotPassword">
            <div v-if="errorMessage" class="error-message">
              <i class="fas fa-exclamation-circle"></i>
              {{ errorMessage }}
            </div>

            <div class="form-group">
              <label for="email">
                <i class="fas fa-envelope"></i>
                Email Address
              </label>
              <input
                id="email"
                v-model="email"
                type="email"
                required
                :disabled="loading"
                placeholder="Enter your email"
                autocomplete="email"
                class="form-input"
              >
            </div>

            <!-- Human Verification -->
            <Captcha 
              ref="captcha"
              @verified="handleCaptchaVerified"
              @failed="handleCaptchaFailed"
              v-if="email && email.includes('@')"
            />

            <button
              type="submit"
              :disabled="loading || !email || !captchaVerified"
              class="btn btn-primary btn-block"
            >
              <i v-if="loading" class="fas fa-spinner fa-spin"></i>
              <span v-else>
                <i class="fas fa-paper-plane"></i>
                Send Reset Link
              </span>
            </button>
          </form>
        </div>

        <div v-else class="success-message">
          <i class="fas fa-check-circle"></i>
          <h3>Email Sent!</h3>
          <p>
            We've sent a password reset link to your email address. Please check your inbox and follow the instructions.
          </p>
          <p class="email-highlight">{{ email }}</p>
          <button @click="handleResend" :disabled="loading" class="btn btn-secondary">
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <span v-else>
              <i class="fas fa-redo"></i>
              Resend Email
            </span>
          </button>
        </div>

        <div class="auth-footer">
          <p>
            Remember your password?
            <a href="#" @click.prevent="$emit('show-login')" class="auth-link">
              Back to login
            </a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { authService } from '@/services/auth-api'
import Captcha from '@/components/Captcha.vue'

export default {
  name: 'ForgotPasswordModal',
  components: {
    Captcha
  },
  emits: ['close', 'show-login'],
  data() {
    return {
      email: '',
      loading: false,
      errorMessage: '',
      emailSent: false,
      captchaVerified: false,
      captchaData: null
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    
    handleCaptchaVerified(captchaData) {
      this.captchaVerified = true
      this.captchaData = captchaData
      this.errorMessage = ''
      // Captcha verified successfully
    },
    
    handleCaptchaFailed(failureData) {
      this.captchaVerified = false
      this.captchaData = null
      this.errorMessage = 'Too many incorrect captcha attempts. Please refresh the page and try again.'
      // Captcha verification failed
      
      this.showNotification({
        type: 'error',
        message: 'Human verification failed. Please try again.'
      })
    },
    
    async handleForgotPassword() {
      // Client-side validation
      if (!this.email || !this.email.trim()) {
        this.errorMessage = 'Please enter your email address.'
        return
      }
      
      if (!this.email.includes('@') || !this.email.includes('.')) {
        this.errorMessage = 'Please enter a valid email address.'
        return
      }
      
      if (!this.captchaVerified) {
        this.errorMessage = 'Please complete the human verification first.'
        return
      }
      
      this.errorMessage = ''
      this.loading = true
      
      try {
        // Send password reset request - API expects username field
        await authService.requestPasswordReset({
          email: this.email,
          captcha: this.captchaData,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent
        })
        
        this.emailSent = true
        
        this.showNotification({
          type: 'success',
          message: 'Password reset link sent to your email!'
        })
        
      } catch (error) {
        console.error('Password reset error:', error)
        
        // Handle API validation errors properly
        let errorMessage = 'Failed to send reset email. Please try again.'
        
        if (error.response?.status === 429) {
          errorMessage = 'Too many requests. Please wait a few minutes before trying again.'
        } else if (error.response?.status === 401) {
          // API returns 401 for "user don't exist"
          if (error.response.data?.message === "user don't exist") {
            errorMessage = 'No account found with this email address. Please check your email or create a new account.'
          } else {
            errorMessage = error.response.data?.message || 'Authentication failed.'
          }
        } else if (error.response?.status === 400 || error.response?.data?.message === 'Validation errors') {
          // Handle validation errors from API
          const validationErrors = error.response.data?.data
          if (validationErrors && typeof validationErrors === 'object') {
            const fieldErrors = Object.values(validationErrors)
            errorMessage = fieldErrors.length > 0 ? fieldErrors[0] : 'Please check your input.'
          } else {
            errorMessage = error.response.data?.message || 'Invalid request. Please check your input.'
          }
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.errorMessage = errorMessage
        
        this.showNotification({
          type: 'error',
          message: errorMessage
        })
        
        // Reset captcha on error to require re-verification
        this.captchaVerified = false
        this.captchaData = null
        if (this.$refs.captcha) {
          this.$refs.captcha.reset()
        }
      } finally {
        this.loading = false
      }
    },
    
    async handleResend() {
      // For resend, we'll use a simpler verification
      this.loading = true
      
      try {
        // Resend uses same API - just the email/username
        await authService.requestPasswordReset(this.email)
        
        this.showNotification({
          type: 'success',
          message: 'Password reset link resent successfully!'
        })
        
      } catch (error) {
        console.error('Resend error:', error)
        
        let errorMessage = 'Failed to resend email. Please try again later.'
        
        if (error.response?.status === 429) {
          errorMessage = 'Please wait before requesting another email.'
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        }
        
        this.showNotification({
          type: 'error',
          message: errorMessage
        })
      } finally {
        this.loading = false
      }
    },
    
    // Reset form when modal is opened
    resetForm() {
      this.email = ''
      this.emailSent = false
      this.errorMessage = ''
      this.captchaVerified = false
      this.captchaData = null
      this.loading = false
      
      if (this.$refs.captcha) {
        this.$refs.captcha.reset()
      }
    }
  },
  
  watch: {
    // Reset form when modal is shown
    '$parent.showAuthModal'(newVal) {
      if (newVal) {
        this.resetForm()
      }
    }
  }
}
</script>

<style scoped>
.auth-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.auth-modal-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideIn 0.3s ease;
}

.auth-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.auth-modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.auth-modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.auth-modal-close:hover {
  color: #1f2937;
}

.auth-form {
  padding: 24px;
}

.form-description {
  color: #6b7280;
  margin-bottom: 24px;
  line-height: 1.6;
}

.error-message {
  background-color: #fee;
  color: #dc2626;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
}

.success-message {
  text-align: center;
  padding: 20px;
}

.success-message i {
  font-size: 3rem;
  color: #22c55e;
  margin-bottom: 16px;
}

.success-message h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
}

.success-message p {
  color: #6b7280;
  margin-bottom: 20px;
  line-height: 1.6;
}

.email-highlight {
  font-weight: 600;
  color: #1a7fd4;
  margin-bottom: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  font-size: 0.875rem;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #1a7fd4;
  box-shadow: 0 0 0 3px rgba(26, 127, 212, 0.1);
}

.form-input:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary {
  background-color: #1a7fd4;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0d6ebd;
}

.btn-primary:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-block {
  width: 100%;
}

.auth-footer {
  margin-top: 24px;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

.auth-link {
  color: #1a7fd4;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
}

.auth-link:hover {
  color: #0d6ebd;
  text-decoration: underline;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@media (max-width: 640px) {
  .auth-modal-container {
    width: 95%;
    margin: 16px;
  }
  
  .auth-modal-header,
  .auth-form {
    padding: 20px;
  }
}
</style>